import type { CelestialType, GameAction } from "../shared/game-types";
import { useGame, useLiveGameTime } from "../app/GameContext";
import { DEFAULT_SECONDS_PER_GAME_YEAR } from "../game-data/balance";
import { CAPABILITY_LABELS } from "../game-data/terminology";

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: React.ReactNode;
  title: string;
  description: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Grade({ label, value }: { label: string; value: number | string }) {
  return <span className="grade" aria-label={`${label} ${value}`}><b>{label}</b><span>{value}</span></span>;
}

export function Meter({
  value,
  max = 100,
  label,
  tone,
}: {
  value: number;
  max?: number;
  label: string;
  tone?: "energy" | "materials" | "research";
}) {
  const percentage = max > 0 ? Math.max(0, Math.min(100, value / max * 100)) : 0;
  return (
    <div
      className={`meter ${tone ? `tone-${tone}` : ""}`}
      data-tone={tone}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.max(0, Math.min(max, value))}
      title={`${label} ${formatNumber(value)} / ${formatNumber(max)}`}
    >
      <span style={{ width: `${percentage}%` }} />
      <span className="sr-only">{label}：{Math.round(percentage)}%</span>
    </div>
  );
}

export function ActionButton({
  action,
  children,
  className = "button",
  disabled = false,
  confirm,
  describedBy,
}: {
  action: GameAction | (() => GameAction);
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  confirm?: string;
  describedBy?: string;
}) {
  const game = useGame();
  const type = typeof action === "function" ? undefined : action.type;
  return (
    <button
      type="button"
      className={className}
      data-action={type}
      aria-describedby={describedBy}
      disabled={disabled || game.busy}
      onClick={() => {
        if (confirm && game.state?.player.settings.confirmDangerous !== false && !window.confirm(confirm)) return;
        void game.perform(typeof action === "function" ? action() : action);
      }}
    >
      {game.busy ? "处理中…" : children}
    </button>
  );
}

export function EmptyState({ children }: { children: React.ReactNode }) {
  return <p className="empty-state">{children}</p>;
}

export interface ChoiceOption<T extends string> {
  value: T;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  tone?: "energy" | "materials" | "research";
}

export function ChoiceGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  variant = "compact",
  className = "",
}: {
  label: string;
  value: T;
  options: Array<ChoiceOption<T>>;
  onChange: (value: T) => void;
  variant?: "compact" | "cards" | "rows";
  className?: string;
}) {
  return (
    <fieldset className={`choice-group choice-${variant} ${className}`.trim()}>
      <legend className="field-label">{label}</legend>
      <div>
        {options.map((option) => (
          <button
            type="button"
            key={option.value}
            className={option.tone ? `tone-${option.tone}` : undefined}
            aria-pressed={value === option.value}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
          >
            <b>{option.label}</b>
            {option.description !== undefined && <small>{option.description}</small>}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export function formatNumber(value: number, maximumFractionDigits = 1) {
  return new Intl.NumberFormat("zh-CN", { notation: Math.abs(value) >= 100_000 ? "compact" : "standard", maximumFractionDigits }).format(value);
}

const GAME_DAYS_PER_MONTH = 30;
const GAME_MONTHS_PER_YEAR = 12;
const GAME_DAYS_PER_YEAR = GAME_DAYS_PER_MONTH * GAME_MONTHS_PER_YEAR;

function safeGameYears(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

export function formatGameDate(value: number, compact = false) {
  const totalDays = Math.floor(safeGameYears(value) * GAME_DAYS_PER_YEAR + 1e-7);
  const year = Math.floor(totalDays / GAME_DAYS_PER_YEAR);
  const dayOfYear = totalDays % GAME_DAYS_PER_YEAR;
  const month = Math.floor(dayOfYear / GAME_DAYS_PER_MONTH) + 1;
  const day = dayOfYear % GAME_DAYS_PER_MONTH + 1;
  return compact ? `第${year}年${month}月${day}日` : `第 ${year} 年 ${month} 月 ${day} 日`;
}

export function formatGameDuration(value: number) {
  const years = safeGameYears(value);
  const maximumFractionDigits = years > 0 && years < 0.1 ? 3 : years < 10 ? 2 : 1;
  return `${formatNumber(years, maximumFractionDigits)} 游戏年`;
}

export function formatRealDuration(gameYears: number, secondsPerGameYear = DEFAULT_SECONDS_PER_GAME_YEAR) {
  if (!(gameYears > 0)) return "0分钟";
  const totalMinutes = Math.max(1, Math.ceil(gameYears * secondsPerGameYear / 60));
  if (totalMinutes < 60) return `${totalMinutes}分钟`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) return minutes ? `${hours}小时${minutes}分钟` : `${hours}小时`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours ? `${days}天${remainingHours}小时` : `${days}天`;
}

export function formatDurationWithReal(gameYears: number, secondsPerGameYear = DEFAULT_SECONDS_PER_GAME_YEAR) {
  return `${formatGameDuration(gameYears)}（现实约 ${formatRealDuration(gameYears, secondsPerGameYear)}）`;
}

export function formatDeadline(deadline: number, now: number, secondsPerGameYear = DEFAULT_SECONDS_PER_GAME_YEAR) {
  const remaining = Math.max(0, deadline - now);
  return remaining > 1e-7
    ? `${formatGameDate(deadline)} · 剩余 ${formatDurationWithReal(remaining, secondsPerGameYear)}`
    : `${formatGameDate(deadline)} · 等待服务器结算`;
}

export function progressBetween(start: number, complete: number, now: number) {
  const duration = Math.max(complete - start, Number.EPSILON);
  return Math.max(0, Math.min(1, (now - start) / duration));
}

export function LiveGameTime({ compact = false }: { compact?: boolean }) {
  const gameTime = useLiveGameTime();
  const text = formatGameDate(gameTime, compact);
  return <time data-live-game-time aria-label={`当前游戏时间 ${formatGameDate(gameTime)}`}>{text}</time>;
}

export const labels = {
  levels: CAPABILITY_LABELS,
  branches: { industry: "工业", navigation: "航行", military: "军事" },
  materials: { structural: "结构材料", highTemp: "高温材料", nuclear: "核材料", superconductor: "超导材料", rare: "稀有材料" },
  installations: {
    probeStation: "自动探测站",
    miningOutpost: "无人采矿站",
    atmosphericCollector: "大气采集站",
    orbitalProcessor: "轨道加工站",
    researchBase: "科研基地",
    industrialBase: "工业基地",
    colony: "殖民地",
  },
  bodyTypes: {
    rocky: "岩石行星",
    metallic: "金属天体",
    iceMoon: "冰质卫星",
    gasGiant: "气态巨行星",
    iceGiant: "冰巨行星",
    lava: "熔岩行星",
    comet: "彗星",
    rogue: "流浪天体",
  } satisfies Record<CelestialType, string>,
  ships: { probe: "探测器", transport: "运输船", heavyTransport: "重型运输船", colony: "殖民船", patrol: "巡逻舰" },
  missions: { survey: "勘测", transport: "运输", relocation: "空载调动", colony: "殖民", escort: "护航", combat: "战斗" },
  status: {
    idle: "待命", mission: "任务中", damaged: "受损", building: "建造中",
    enRoute: "航行中", complete: "完成", failed: "失败", returning: "返航中",
  },
} as const;
